import { memo } from "react";
import type { MouseEvent } from "react";
import clsx from "clsx";
import { Check, ImageOff } from "lucide-react";
import { TruncatedTextTooltip } from "components/common/TruncatedTextTooltip";
import { useGridTileImage } from "../useGridTileImage";
import type { GridReviewTileProps } from "./types";

const GridReviewTileComponent = ({
  element,
  orderNumber,
  imageUrl,
  pageScaleFactor,
  sourceWidthPx,
  sourceHeightPx,
  isSelected,
  isSelectable,
  showGroupCaption,
  loadPageImage,
  onToggle,
  onJump,
}: GridReviewTileProps) => {
  const { tileRef, tileImageDataUrl, hasTileImageFailed } = useGridTileImage({
    imageUrl,
    bbox: element.bbox,
    pageScaleFactor,
    sourceWidthPx,
    sourceHeightPx,
    loadPageImage,
  });

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    // The first click of a double-click would otherwise toggle a phantom selection.
    if (!isSelectable || event.detail > 1) {
      return;
    }
    onToggle(element.id, event.shiftKey);
  };

  const handleDoubleClick = () => {
    onJump(element.id);
  };

  const [red, green, blue] = element.rgb;

  return (
    <div
      ref={tileRef}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      className={clsx(
        "group relative flex cursor-pointer select-none flex-col overflow-hidden rounded-lg border bg-white transition-transform duration-200 hover:z-10 hover:scale-110 hover:shadow-md",
        isSelected
          ? "border-blue-500 ring-2 ring-blue-500"
          : "border-gray-200 hover:border-gray-400",
      )}
    >
      {isSelectable && (
        <span
          className={clsx(
            "absolute left-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded border transition-colors",
            isSelected
              ? "border-blue-500 bg-blue-500 text-white"
              : "border-gray-300 bg-white text-transparent group-hover:border-blue-400",
          )}
        >
          <Check size={14} strokeWidth={3} />
        </span>
      )}
      <div className="flex aspect-square w-full items-center justify-center bg-gray-50">
        {tileImageDataUrl !== null ? (
          <img
            src={tileImageDataUrl}
            alt=""
            draggable={false}
            className="h-full w-full object-contain"
          />
        ) : hasTileImageFailed ? (
          <ImageOff size={20} className="text-gray-300" />
        ) : null}
      </div>
      <div className="flex items-center gap-1 border-t border-gray-100 px-1.5 py-1">
        <span className="text-xs text-gray-500">#{orderNumber}</span>
        {showGroupCaption && (
          <>
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: `rgb(${red}, ${green}, ${blue})` }}
            />
            <TruncatedTextTooltip
              text={element.group_id}
              className="min-w-0 text-xs text-gray-600"
            />
          </>
        )}
      </div>
    </div>
  );
};

export const GridReviewTile = memo(GridReviewTileComponent);
